import { ChevronDown, ChevronUp } from '@untitledui/icons';
import { memo, useCallback, useState } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import type { CrmObject } from '@/types/crm';

import type { MeetingSummaryItem } from '../../lib/crm/utils/meetingSeriesGrouping';
import { MeetingSummaryCard } from './MeetingSummaryCard';

interface MeetingSeriesCardProps {
  item: Extract<MeetingSummaryItem, { kind: 'series' }>;
  onCardClick: (note: CrmObject) => void;
}

export const MeetingSeriesCard = memo(function MeetingSeriesCard({
  item,
  onCardClick,
}: MeetingSeriesCardProps) {
  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback(() => setExpanded(prev => !prev), []);

  const count = item.occurrences.length;
  const hidden = count - 1;
  const visible = expanded ? item.occurrences : item.occurrences.slice(0, 1);

  return (
    <div
      data-component='meeting-series-card'
      data-state={expanded ? 'open' : 'closed'}
      className='rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3'
    >
      <div className='flex items-center justify-between mb-2'>
        <Badge size='sm' color='gray'>
          {count} meetings
        </Badge>
        <button
          type='button'
          onClick={toggle}
          aria-expanded={expanded}
          className='flex items-center gap-1 text-xs text-fg-secondary hover:text-fg-primary cursor-pointer bg-transparent border-none p-0'
        >
          {expanded ? 'Hide' : `Show ${hidden} earlier`}
          {expanded ? (
            <ChevronUp className='size-4' />
          ) : (
            <ChevronDown className='size-4' />
          )}
        </button>
      </div>
      <div className='flex flex-col gap-2'>
        {visible.map(o => (
          <MeetingSummaryCard
            key={o.note.entity_id}
            note={o.note}
            meeting={o.meeting}
            onClick={onCardClick}
          />
        ))}
      </div>
    </div>
  );
});
